import { useState } from "react";
import { G, GL, GB, BLACK, MID, LIGHT, BORDER, BG, BENCHMARKS, calcRevenue } from "../constants";
import { Ring, Tag, SourceLogo, ProgressBar, renderWithExclusions } from "../components/shared";
import { SOURCE_META, CHANNEL_FORMATS } from "../data";
import { PreviewPanels } from "../components/PreviewPanels";
import { PageHead, Footer } from "../components/Shell";

export function S05({ sel, fixes, appr, setAppr, onBack, onNext, analyticsConnected, realCVR, realAOV, products, generatedCopy = {} }) {
  const [open, setOpen] = useState({});
  const [openPrev, setOpenPrev] = useState({});
  const [excluded, setExcluded] = useState({}); // { [pid]: [phrase, ...] }
  const [channelTab, setChannelTab] = useState({}); // { [pid]: "shopping"|"seo"|"meta"|"tiktok" }

  // Helper: get optimised copy (AI-generated > pre-set > original)
  const getTitle = (p) => generatedCopy[p.id]?.optimised_title || p.optimisedTitle || p.title;
  const getDesc = (p) => generatedCopy[p.id]?.optimised_description || p.optimisedDesc || p.description || "";

  const prods  = products.filter(p => sel.includes(p.id));
  const totRev = prods.reduce((s, p) => s + p.impact.revenue, 0);
  const totImp = prods.reduce((s, p) => s + (p.impact.impAfter - p.impact.impBefore), 0);

  const toggleExclude = (pid, phrase) => {
    const current = excluded[pid] || [];
    const next = current.includes(phrase)
      ? current.filter(x => x !== phrase)
      : [...current, phrase];
    setExcluded({ ...excluded, [pid]: next });
  };

  const isExcluded = (pid, phrase) => (excluded[pid] || []).includes(phrase);

  // Apply exclusions to copy - strike out excluded phrases with a visual marker
  const applyExclusions = (text, pid) => {
    const exList = excluded[pid] || [];
    if (!exList.length) return { text, hasExclusions: false };
    let result = text;
    exList.forEach(phrase => {
      const regex = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi");
      result = result.replace(regex, `[[EXCLUDED:${phrase}]]`);
    });
    return { text: result, hasExclusions: true };
  };

  const allApproved = appr.length === prods.length && prods.length > 0;

  return (
    <>
      <PageHead num="07" title="Review & Approve" sub="Review feed intelligence output per SKU. Approve to push optimised copy and GMC fixes to Shopify simultaneously." />

      {/* ── Top action bar ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, padding: "10px 16px", background: "#fafafa", border: `1px solid ${BORDER}`, borderRadius: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => setAppr(allApproved ? [] : prods.map(p => p.id))}
            style={{ background: allApproved ? BG : G, color: allApproved ? G : "#fff", border: `1px solid ${allApproved ? GB : G}`, borderRadius: 6, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {allApproved ? "✓ All approved - deselect" : `Approve all (${prods.length})`}
          </button>
          <span style={{ fontSize: 12, color: LIGHT }}>{appr.length} of {prods.length} approved</span>
        </div>
        <button onClick={onNext} disabled={appr.length === 0}
          style={{ background: appr.length === 0 ? "#e5e7eb" : G, color: appr.length === 0 ? LIGHT : "#fff", border: "none", borderRadius: 6, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: appr.length === 0 ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
          Push {appr.length > 0 ? appr.length : ""} SKU{appr.length !== 1 ? "s" : ""} live →
        </button>
      </div>

      {/* ── Pre-push summary ── */}
      {appr.length > 0 && (() => {
        const approvedProds = products.filter(p => appr.includes(p.id));
        const totalFields   = approvedProds.length * 4; // title, desc, meta title, meta desc per SKU
        const totalSignals  = approvedProds.reduce((s, p) => s + (p.signalAttribution?.length || 0), 0);
        const totalGmcFixes = Object.entries(fixes).filter(([k, v]) => approvedProds.some(p => k.startsWith(`${p.id}-`)) && v).length;
        const approvedRev   = approvedProds.reduce((s, p) => s + p.impact.revenue, 0);
        return (
          <div style={{ border: `1px solid ${GB}`, borderRadius: 8, background: GL, padding: "12px 16px", marginBottom: 8, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: G, marginRight: 4 }}>Pushing to Shopify:</span>
            {[
              { val: `${totalFields} fields`, label: "optimised" },
              { val: `${totalSignals} signals`, label: "embedded" },
              ...(totalGmcFixes > 0 ? [{ val: `${totalGmcFixes} GMC fixes`, label: "applied" }] : []),
              { val: `£${approvedRev.toLocaleString()}/mo`, label: "est. uplift" },
            ].map((item, i) => (
              <span key={`stat-${item.label}`} style={{ fontSize: 11, background: BG, border: `1px solid ${GB}`, borderRadius: 4, padding: "3px 9px", color: BLACK }}>
                <strong>{item.val}</strong> <span style={{ color: MID }}>{item.label}</span>
              </span>
            ))}
            <span style={{ fontSize: 11, color: MID, marginLeft: "auto", fontStyle: "italic" }}>Est. based on {analyticsConnected ? `your store data (CVR ${(realCVR*100).toFixed(1)}%, AOV £${realAOV})` : "industry benchmarks - connect Shopify Analytics for real figures"}</span>
          </div>
        );
      })()}

      {/* Batch summary bar */}
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
        <div style={{ display: "flex" }}>
          {[
            { l: "Est. revenue uplift / mo", v: `£${totRev.toLocaleString()}`, c: G },
            { l: "Extra impressions / mo",   v: `+${totImp.toLocaleString()}`, c: "#4285f4" },
            { l: "SKUs approved",            v: `${appr.length} / ${prods.length}`, c: BLACK },
          ].map((x, i) => (
            <div key={x.l} style={{ flex: 1, padding: "12px 18px", borderRight: i < 2 ? `1px solid ${BORDER}` : "none", background: "#fafafa" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: x.c }}>{x.v}</div>
              <div style={{ fontSize: 10, color: LIGHT, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 600 }}>{x.l}</div>
            </div>
          ))}
        </div>
        {/* Benchmark footnote */}
        <div style={{ padding: "8px 18px", borderTop: `1px solid ${BORDER}`, background: "#fffbeb", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>Industry benchmarks applied:</span>
          {[
            { label: "AOV", val: `£${BENCHMARKS.AOV}`, tip: "UK DTC avg AOV (IRP Commerce 2025)" },
            { label: "SEO CTR", val: `${(BENCHMARKS.SEO_CTR*100).toFixed(0)}%`, tip: "Organic position-1 CTR (Sistrix/Ahrefs 2025)" },
            { label: "PLA CTR", val: `${(BENCHMARKS.PLA_CTR*100).toFixed(2)}%`, tip: "Google Shopping CTR (Bind Media 2025)" },
            { label: "CVR", val: `${(BENCHMARKS.CVR*100).toFixed(1)}%`, tip: "DTC session-to-purchase CVR midpoint (IRP Commerce/Shopify)" },
          ].map(b => (
            <span key={b.label} title={b.tip} style={{ fontSize: 11, color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 4, padding: "2px 8px", cursor: "help" }}>
              {b.label}: <strong>{b.val}</strong>
            </span>
          ))}
          <span style={{ fontSize: 11, color: "#92400e", marginLeft: "auto", fontStyle: "italic" }}>In production, these pull from your Shopify analytics</span>
        </div>
      </div>

      <div style={{ marginBottom: 16 }} />

      {prods.map(p => {
        const isAppr = appr.includes(p.id);
        const isOpen = open[p.id];
        const gmcN   = p.gmcIssues.filter(i => fixes[`${p.id}-${i.field}`]).length;
        const pct    = v => `+${Math.round(((v[1] - v[0]) / Math.max(v[0], 1)) * 100)}%`;

        // Derived metric data matching screenshot layout
        const metrics = [
          {
            label: "IMPRESSIONS", color: "#4285f4",
            big: p.impact.impAfter.toLocaleString(),
            pct: pct([p.impact.impBefore, p.impact.impAfter]),
            sub: `from ${p.impact.impBefore.toLocaleString()}`,
            note: "Based on new keyword targets vs GSC baseline",
            barBefore: p.impact.impBefore, barAfter: p.impact.impAfter, barMax: p.impact.impAfter,
          },
          {
            label: "CLICKS", color: "#34a853",
            big: p.impact.clkAfter.toLocaleString(),
            pct: pct([p.impact.clkBefore, p.impact.clkAfter]),
            sub: `from ${p.impact.clkBefore.toLocaleString()}`,
            note: `SEO CTR ${(BENCHMARKS.SEO_CTR*100).toFixed(0)}% (pos.1) x impressions, blended with PLA ${(BENCHMARKS.PLA_CTR*100).toFixed(2)}%`,
            barBefore: p.impact.clkBefore, barAfter: p.impact.clkAfter, barMax: p.impact.clkAfter,
          },
          {
            label: "UPLIFT / MO", color: "#d97706",
            big: `£${p.impact.revenue.toLocaleString()}`,
            pct: `+${Math.round(((p.impact.clkAfter - p.impact.clkBefore) * BENCHMARKS.CVR * BENCHMARKS.AOV) / Math.max(p.impact.clkBefore * BENCHMARKS.CVR * BENCHMARKS.AOV, 1) * 100)}%`,
            sub: "vs current baseline",
            note: `${p.impact.clkAfter - p.impact.clkBefore} extra clicks x ${(BENCHMARKS.CVR*100).toFixed(1)}% CVR x £${BENCHMARKS.AOV} AOV`,
            barBefore: 0, barAfter: p.impact.revenue, barMax: p.impact.revenue,
          },
          {
            label: "KEYWORDS", color: "#6c63ff",
            big: p.impact.keywords,
            pct: pct([Math.max(p.impact.keywords - 5, 1), p.impact.keywords]),
            sub: `from ${Math.max(p.impact.keywords - 5, 1)}`,
            note: "Long-tail terms newly integrated",
            barBefore: Math.max(p.impact.keywords - 5, 1), barAfter: p.impact.keywords, barMax: p.impact.keywords,
          },
        ];

        return (
          <div key={p.id} style={{ border: `1px solid ${isAppr ? GB : BORDER}`, borderRadius: 8, overflow: "hidden", marginBottom: 12, borderLeft: `3px solid ${isAppr ? G : BORDER}` }}>

            {/* ── Collapsed header ── */}
            <div style={{ padding: "12px 16px", background: isAppr ? GL : "#fafafa", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{p.title}</span>
                  <span style={{ fontSize: 11, color: LIGHT }}>{p.sku}</span>
                  {gmcN > 0 && <Tag>{gmcN} GMC fix{gmcN > 1 ? "es" : ""}</Tag>}
                  {isAppr && <Tag>✓ Approved</Tag>}
                  {(excluded[p.id] || []).length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 4, padding: "1px 7px" }}>
                      {(excluded[p.id] || []).length} phrase{(excluded[p.id] || []).length !== 1 ? "s" : ""} excluded
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setOpen({ ...open, [p.id]: !isOpen })}
                style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 5, padding: "5px 12px", fontSize: 12, color: MID, cursor: "pointer", fontFamily: "inherit" }}>
                {isOpen ? "Hide details ↑" : "Show details ↓"}
              </button>
              <button onClick={() => setAppr(isAppr ? appr.filter(x => x !== p.id) : [...appr, p.id])}
                style={{ background: isAppr ? BG : G, color: isAppr ? G : "#fff", border: `1px solid ${isAppr ? GB : G}`, borderRadius: 6, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", minWidth: 96 }}>
                {isAppr ? "✓ Approved" : "Approve"}
              </button>
            </div>

            {/* ── Expanded detail panel ── */}
            {isOpen && (
              <div style={{ borderTop: `1px solid ${BORDER}` }}>

                {/* Metric cards row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", borderBottom: `1px solid ${BORDER}` }}>
                  {metrics.map((m, i) => (
                    <div key={m.label} style={{ padding: "14px 16px", borderRight: `1px solid ${BORDER}` }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: LIGHT, letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 6 }}>{m.label}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 22, fontWeight: 700, color: BLACK }}>{m.big}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: G }}>{m.pct}</span>
                      </div>
                      <div style={{ fontSize: 11, color: LIGHT, marginBottom: 2 }}>{m.sub}</div>
                      <ProgressBar before={m.barBefore} after={m.barAfter} max={m.barMax} color={m.color} />
                      <div style={{ fontSize: 11, color: MID, marginTop: 6, lineHeight: 1.5 }}>{m.note}</div>
                    </div>
                  ))}

                  {/* GMC score tile */}
                  <div style={{ padding: "14px 16px" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: LIGHT, letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 8 }}>GMC SCORE</div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: LIGHT, marginBottom: 4 }}>Before</div>
                        <Ring score={p.impact.scoreBefore} size={44} />
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: LIGHT, marginBottom: 4 }}>After</div>
                        <Ring score={p.impact.scoreAfter} size={44} />
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#6c63ff" }}>+{p.impact.scoreAfter - p.impact.scoreBefore} pts</div>
                    <div style={{ fontSize: 11, color: MID, marginTop: 3, lineHeight: 1.5 }}>Title structure, attribute completeness & keyword alignment</div>
                  </div>
                </div>

                {/* ── Signal Freshness Bar ── */}
                <div style={{ padding: "10px 16px", borderBottom: `1px solid ${BORDER}`, background: "#fafafa", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.6px" }}>Signals scanned</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: MID }}>{p.signalFreshness?.lastScanned ?? "recently"}</span>
                  <span style={{ color: BORDER, fontSize: 11 }}>·</span>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {p.signalFreshness && Object.entries(p.signalFreshness.sources || {}).map(([src, age]) => (
                      <span key={src} title={`${SOURCE_META[src]?.label || src}: ${age} ago`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 3, background: "#f3f4f6", border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 6px" }}>
                        <SourceLogo src={src} size={13} />
                        <span style={{ fontSize: 10, color: LIGHT, fontWeight: 600 }}>{age}</span>
                      </span>
                    ))}
                  </div>
                  <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: G, background: GL, border: `1px solid ${GB}`, borderRadius: 4, padding: "2px 8px" }}>● Live</span>
                </div>

                {/* ── Shopify Product Data + Signal Match ── */}
                <div style={{ borderBottom: `1px solid ${BORDER}` }}>
                  {/* Header row */}
                  <div style={{ padding: "12px 16px 10px", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, letterSpacing: "0.8px", textTransform: "uppercase" }}>Shopify Product Data</div>
                    <span style={{ fontSize: 10, color: LIGHT }}>-</span>
                    <div style={{ fontSize: 11, color: LIGHT }}>attributes matched intelligently against live signals</div>
                    <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      {[["matched","#2a6b45","#f0f7f3","#d4ead9"],["inferred","#d97706","#fffbeb","#fde68a"],["gap","#dc2626","#fef2f2","#fecaca"]].map(([label, color, bg, border]) => (
                        <span key={label} style={{ fontSize: 10, fontWeight: 700, color, background: bg, border: `1px solid ${border}`, borderRadius: 4, padding: "2px 7px", textTransform: "capitalize" }}>{label}</span>
                      ))}
                    </div>
                  </div>

                  {/* Two columns: raw Shopify fields left, attribute matches right */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", borderTop: `1px solid ${BORDER}` }}>

                    {/* LEFT: raw Shopify data */}
                    <div style={{ padding: "14px 16px", borderRight: `1px solid ${BORDER}`, background: "#fafafa" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10 }}>Raw Shopify fields</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {p.shopifyData && Object.entries(p.shopifyData.metafields || {}).map(([k, v]) => (
                          <div key={k} style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", minWidth: 72, letterSpacing: "0.3px" }}>{k}</span>
                            <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>{v}</span>
                          </div>
                        ))}
                        {p.shopifyData && (
                          <div style={{ display: "flex", gap: 6, alignItems: "baseline", marginTop: 4, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", minWidth: 72, letterSpacing: "0.3px" }}>Sizes</span>
                            <span style={{ fontSize: 12, color: "#374151" }}>{p.shopifyData.variants.map(v => v.size).join(", ")}</span>
                          </div>
                        )}
                        {p.shopifyData && (
                          <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", minWidth: 72, letterSpacing: "0.3px" }}>Tags</span>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {p.shopifyData.tags.map(t => (
                                <span key={t} style={{ fontSize: 10, background: "#f3f4f6", border: `1px solid ${BORDER}`, color: MID, padding: "1px 6px", borderRadius: 3 }}>{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RIGHT: attribute match rows */}
                    <div style={{ padding: "14px 16px" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10 }}>Signal-to-attribute match</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(p.attributeMatches || []).map((m, i) => {
                          const colors = {
                            verified: { border: "#d4ead9", bg: "#f0f7f3",  text: "#2a6b45", label: "Verified" },
                            inferred: { border: "#fde68a", bg: "#fffbeb",  text: "#92400e", label: "Inferred" },
                            conflict: { border: "#fecaca", bg: "#fef2f2",  text: "#dc2626", label: "Conflict" },
                          }[m.status] || { border: "#e5e7eb", bg: "#f9fafb", text: "#6b7280", label: m.status };
                          return (
                            <div key={`attr-${m.field}-${m.status}`} style={{ border: `1px solid ${colors.border}`, borderLeft: `3px solid ${colors.text}`, borderRadius: 6, padding: "10px 12px", background: colors.bg }}>
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 5 }}>
                                {/* Field + Shopify val */}
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                                    <span style={{ fontSize: 10, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px" }}>{m.field}</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: colors.text, background: "rgba(255,255,255,0.6)", border: `1px solid ${colors.border}`, borderRadius: 3, padding: "1px 6px" }}>{colors.label}</span>
                                  </div>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 11, color: "#374151" }}>
                                      <span style={{ color: LIGHT }}>Shopify: </span>
                                      <strong>{m.shopifyVal ?? <span style={{ color: "#dc2626", fontStyle: "italic" }}>not set</span>}</strong>
                                    </span>
                                    {m.signalMatch && <>
                                      <span style={{ color: BORDER, fontSize: 12 }}>-</span>
                                      <span style={{ fontSize: 11, color: "#374151" }}>
                                        <span style={{ color: LIGHT }}>Signal: </span>
                                        <strong style={{ color: colors.text }}>"{m.signalMatch}"</strong>
                                      </span>
                                    </>}
                                  </div>
                                </div>
                                {/* Source logos */}
                                <div style={{ display: "flex", gap: 3, flexShrink: 0, paddingTop: 2 }}>
                                  {m.sources.map(s => <span key={s} title={SOURCE_META[s]?.label}><SourceLogo src={s} size={15} /></span>)}
                                </div>
                              </div>
                              {m.status === "conflict" && (
                                <div data-testid="conflict-warning" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, padding: "5px 8px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 4 }}>
                                  <span style={{ fontSize: 13, lineHeight: 1 }}>&#9888;</span>
                                  <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", letterSpacing: "0.3px" }}>REVIEW BEFORE PUBLISHING - this attribute is excluded from auto-generated copy until resolved</span>
                                </div>
                              )}
                              <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>{m.why}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Signal Attribution + Phrase Exclusion ── */}
                <div style={{ padding: "16px 16px 14px", borderBottom: `1px solid ${BORDER}` }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 2 }}>Signal Attribution - where each phrase came from</div>
                      <div style={{ fontSize: 11, color: LIGHT }}>Click any phrase to exclude it from the generated copy. Excluded phrases won't appear in the title, description or meta fields.</div>
                    </div>
                    {(excluded[p.id] || []).length > 0 && (
                      <button onClick={() => setExcluded({ ...excluded, [p.id]: [] })}
                        style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 5, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", marginLeft: 12 }}>
                        Restore all
                      </button>
                    )}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {(p.signalAttribution || []).map((attr, i) => {
                      const excl = isExcluded(p.id, attr.phrase);
                      return (
                        <div key={`sig-${attr.phrase}`}
                          onClick={() => toggleExclude(p.id, attr.phrase)}
                          style={{
                            border: `1px solid ${excl ? "#fecaca" : attr.sources.length >= 3 ? GB : BORDER}`,
                            borderLeft: `3px solid ${excl ? "#dc2626" : attr.sources.length >= 3 ? G : BORDER}`,
                            borderRadius: 7, padding: "8px 12px",
                            background: excl ? "#fef2f2" : attr.sources.length >= 3 ? "#f8fffe" : "#fafafa",
                            minWidth: 180, flex: "1 1 180px",
                            cursor: "pointer", opacity: excl ? 0.75 : 1,
                            transition: "all 0.15s", position: "relative",
                          }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: excl ? "#dc2626" : BLACK, textDecoration: excl ? "line-through" : "none" }}>"{attr.phrase}"</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: excl ? "#dc2626" : G, background: excl ? "#fef2f2" : GL, border: `1px solid ${excl ? "#fecaca" : GB}`, borderRadius: 3, padding: "1px 6px", marginLeft: 6, whiteSpace: "nowrap" }}>
                              {excl ? "Excluded" : "Include"}
                            </span>
                          </div>
                          <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4 }}>
                            {attr.sources.map(s => <span key={s} title={SOURCE_META[s]?.label}><SourceLogo src={s} size={14} /></span>)}
                            {attr.sources.length >= 3 && !excl && <span style={{ fontSize: 10, fontWeight: 800, color: G, background: GL, border: `1px solid ${GB}`, padding: "1px 5px", borderRadius: 3, marginLeft: 2 }}>multi-source</span>}
                          </div>
                          <div style={{ fontSize: 11, color: LIGHT, lineHeight: 1.4 }}>{attr.note}</div>
                          {excl && (
                            <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: "#dc2626" }}>Click to restore</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(excluded[p.id] || []).length > 0 && (
                    <div style={{ marginTop: 12, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, fontSize: 12, color: "#dc2626" }}>
                      <strong>{(excluded[p.id] || []).length} phrase{(excluded[p.id] || []).length !== 1 ? "s" : ""} excluded:</strong>{" "}
                      {(excluded[p.id] || []).map(ph => `"${ph}"`).join(", ")} - copy will regenerate without these on approval.
                    </div>
                  )}
                </div>

                {/* ── Before / After with inline editing ── */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: `1px solid ${BORDER}` }}>
                  {/* BEFORE */}
                  <div style={{ padding: "18px 20px", borderRight: `1px solid ${BORDER}` }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#f87171", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 14 }}>BEFORE</div>
                    <div style={{ border: `1px solid #fecaca`, borderRadius: 8, padding: "14px 16px", background: "#fffafa" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: "#f87171", letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 5 }}>TITLE</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: BLACK, marginBottom: 12, textTransform: "uppercase" }}>{p.title}</div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: "#f87171", letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 5 }}>DESCRIPTION</div>
                      <div style={{ fontSize: 12, color: LIGHT, fontStyle: "italic" }}>No description</div>
                    </div>
                  </div>

                  {/* AFTER - with inline editing and exclusion preview */}
                  <div style={{ padding: "18px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: G, letterSpacing: "1.5px", textTransform: "uppercase" }}>AFTER</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {(excluded[p.id] || []).length > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 4, padding: "3px 8px" }}>
                            {(excluded[p.id] || []).length} excluded
                          </span>
                        )}
                        <button
                          onClick={() => setOpen({ ...open, [`edit-${p.id}`]: !open[`edit-${p.id}`] })}
                          style={{ fontSize: 11, fontWeight: 700, color: open[`edit-${p.id}`] ? G : MID, background: open[`edit-${p.id}`] ? GL : "none", border: `1px solid ${open[`edit-${p.id}`] ? GB : BORDER}`, borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>
                          {open[`edit-${p.id}`] ? "✓ Editing" : "✏ Edit"}
                        </button>
                      </div>
                    </div>
                    <div style={{ border: `1px solid ${(excluded[p.id] || []).length > 0 ? "#fecaca" : GB}`, borderRadius: 8, padding: "14px 16px", background: (excluded[p.id] || []).length > 0 ? "#fffafa" : GL, transition: "all 0.2s" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: (excluded[p.id] || []).length > 0 ? "#dc2626" : G, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 5 }}>TITLE</div>
                      {open[`edit-${p.id}`]
                        ? <input defaultValue={getTitle(p)}
                            style={{ width: "100%", fontSize: 13, fontWeight: 700, color: BLACK, border: `1px solid ${GB}`, borderRadius: 5, padding: "6px 10px", fontFamily: "inherit", background: "#fff", marginBottom: 12, outline: "none" }} />
                        : <div style={{ fontSize: 13, fontWeight: 700, color: BLACK, marginBottom: 12, lineHeight: 1.4 }}>
                            {renderWithExclusions(getTitle(p), excluded[p.id] || [])}
                          </div>
                      }
                      <div style={{ fontSize: 10, fontWeight: 800, color: (excluded[p.id] || []).length > 0 ? "#dc2626" : G, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 5 }}>DESCRIPTION</div>
                      {open[`edit-${p.id}`]
                        ? <textarea defaultValue={getDesc(p)} rows={6}
                            style={{ width: "100%", fontSize: 12, color: "#374151", border: `1px solid ${GB}`, borderRadius: 5, padding: "8px 10px", fontFamily: "inherit", background: "#fff", lineHeight: 1.6, resize: "vertical", outline: "none" }} />
                        : <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.7 }}>
                            {renderWithExclusions(getDesc(p), excluded[p.id] || [])}
                          </div>
                      }
                    </div>
                    {(excluded[p.id] || []).length > 0 && (
                      <div style={{ marginTop: 8, fontSize: 11, color: "#dc2626", fontStyle: "italic" }}>
                        Excluded phrases shown struck through. Copy will regenerate without them on approval.
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Channel Format Engine ── */}
                <div style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, letterSpacing: "0.8px", textTransform: "uppercase" }}>Channel Format Engine</div>
                    <div data-testid={`channel-tabs-${p.id}`} style={{ display: "flex", gap: 0, border: `1px solid ${BORDER}`, borderRadius: 6, overflow: "hidden" }}>
                      {[
                        { key: "shopping", label: "Google Shopping", active: true },
                        { key: "seo",      label: "SEO / GEO",      active: true },
                        { key: "meta",     label: "Meta Ads",       active: false },
                        { key: "tiktok",   label: "TikTok Shop",    active: false },
                      ].map(ch => {
                        const selected = (channelTab[p.id] || "shopping") === ch.key;
                        return (
                          <button key={ch.key} data-testid={`channel-${ch.key}-${p.id}`}
                            onClick={() => setChannelTab({ ...channelTab, [p.id]: ch.key })}
                            style={{
                              border: "none", borderRight: `1px solid ${BORDER}`,
                              background: selected ? (ch.active ? BLACK : "#6b7280") : BG,
                              color: selected ? "#fff" : ch.active ? MID : LIGHT,
                              padding: "5px 12px", fontSize: 11, fontWeight: 700,
                              cursor: ch.active ? "pointer" : "default",
                              fontFamily: "inherit", transition: "background 0.1s",
                              opacity: ch.active ? 1 : 0.7,
                            }}>
                            {ch.label}
                            {!ch.active && <span style={{ fontSize: 9, fontWeight: 800, color: selected ? "#fde68a" : "#d97706", background: selected ? "rgba(253,230,138,0.2)" : "#fffbeb", border: `1px solid ${selected ? "rgba(253,230,138,0.3)" : "#fde68a"}`, borderRadius: 3, padding: "0px 4px", marginLeft: 4, letterSpacing: "0.4px" }}>BETA</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Shopping variant */}
                  {(channelTab[p.id] || "shopping") === "shopping" && (() => {
                    const aiCopy = generatedCopy[p.id] || {};
                    const demoFmt = CHANNEL_FORMATS[p.id]?.shopping;
                    const hasAI = !!aiCopy.shopping_title;
                    const title = hasAI ? aiCopy.shopping_title : (demoFmt?.title || "");
                    const desc = hasAI ? aiCopy.shopping_description : (demoFmt?.description || "");
                    const attrs = hasAI && aiCopy.gmc_attributes ? aiCopy.gmc_attributes : (demoFmt?.attributes || {});
                    const titleLen = title.length;
                    const descLen = desc.length;
                    if (!title && !demoFmt) return null;
                    return (
                      <div data-testid={`channel-shopping-content-${p.id}`} style={{ padding: "0 16px 16px" }}>
                        {hasAI && <div style={{ marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", background: GL, border: `1px solid ${GB}`, borderRadius: 4, fontSize: 10, fontWeight: 700, color: G }}>AI-generated</div>}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          <div>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                <span style={{ fontSize: 10, fontWeight: 800, color: "#4285f4", letterSpacing: "0.5px", textTransform: "uppercase" }}>Shopping Title</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: titleLen <= 150 ? G : "#dc2626" }}>{titleLen}/150 chars</span>
                              </div>
                              <div style={{ border: `1px solid ${GB}`, borderRadius: 6, padding: "10px 12px", background: GL, fontSize: 12, fontWeight: 600, color: BLACK, lineHeight: 1.5 }}>{title}</div>
                              <div style={{ height: 4, borderRadius: 2, background: "#e5e7eb", marginTop: 6, overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${Math.min(100, (titleLen / 150) * 100)}%`, background: titleLen <= 150 ? G : "#dc2626", borderRadius: 2 }} />
                              </div>
                            </div>
                            <div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                <span style={{ fontSize: 10, fontWeight: 800, color: "#4285f4", letterSpacing: "0.5px", textTransform: "uppercase" }}>Shopping Description</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: G }}>{descLen}/5000 chars</span>
                              </div>
                              <div style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: "10px 12px", background: BG, fontSize: 12, color: MID, lineHeight: 1.6, maxHeight: 160, overflowY: "auto" }}>{desc}</div>
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: "#4285f4", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>GMC Structured Attributes</div>
                            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 6, overflow: "hidden" }}>
                              {Object.entries(attrs || {}).map(([k, v], i, arr) => (
                                <div key={k} style={{ display: "flex", borderBottom: i < arr.length - 1 ? `1px solid ${BORDER}` : "none", padding: "8px 12px", background: i % 2 === 0 ? BG : "#fff" }}>
                                  <span style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.3px", minWidth: 100 }}>{k.replace(/_/g, " ")}</span>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>{v}</span>
                                </div>
                              ))}
                            </div>
                            <div style={{ marginTop: 8, padding: "6px 10px", background: GL, border: `1px solid ${GB}`, borderRadius: 5, fontSize: 11, color: G, fontWeight: 600 }}>
                              {Object.keys(attrs || {}).length >= 6 ? "All required GMC attributes populated - feed-ready" : `${Object.keys(attrs || {}).length} attributes set - review missing fields`}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* SEO / GEO variant */}
                  {(channelTab[p.id]) === "seo" && (() => {
                    const aiCopy = generatedCopy[p.id] || {};
                    const demoFmt = CHANNEL_FORMATS[p.id]?.seo;
                    const hasAI = !!aiCopy.seo_title;
                    const title = hasAI ? aiCopy.seo_title : (demoFmt?.title || "");
                    const metaDesc = hasAI ? aiCopy.seo_meta_description : (demoFmt?.metaDesc || "");
                    const h1 = hasAI ? aiCopy.seo_h1 : (demoFmt?.h1 || "");
                    const longDesc = hasAI ? aiCopy.seo_long_description : (demoFmt?.longDesc || "");
                    const geoTargets = hasAI ? (aiCopy.geo_targets || ["UK"]) : (demoFmt?.geoTargets || ["UK"]);
                    const titleLen = title.length;
                    const metaDescLen = metaDesc.length;
                    if (!title && !demoFmt) return null;
                    return (
                      <div data-testid={`channel-seo-content-${p.id}`} style={{ padding: "0 16px 16px" }}>
                        {hasAI && <div style={{ marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", background: GL, border: `1px solid ${GB}`, borderRadius: 4, fontSize: 10, fontWeight: 700, color: G }}>AI-generated</div>}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          <div>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                <span style={{ fontSize: 10, fontWeight: 800, color: "#34a853", letterSpacing: "0.5px", textTransform: "uppercase" }}>SEO Title (Title Tag)</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: titleLen <= 70 ? G : "#dc2626" }}>{titleLen}/70 chars</span>
                              </div>
                              <div style={{ border: `1px solid ${GB}`, borderRadius: 6, padding: "10px 12px", background: GL, fontSize: 12, fontWeight: 600, color: BLACK, lineHeight: 1.5 }}>{title}</div>
                              <div style={{ height: 4, borderRadius: 2, background: "#e5e7eb", marginTop: 6, overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${Math.min(100, (titleLen / 70) * 100)}%`, background: titleLen <= 70 ? G : "#dc2626", borderRadius: 2 }} />
                              </div>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                <span style={{ fontSize: 10, fontWeight: 800, color: "#34a853", letterSpacing: "0.5px", textTransform: "uppercase" }}>Meta Description</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: metaDescLen <= 160 ? G : "#dc2626" }}>{metaDescLen}/160 chars</span>
                              </div>
                              <div style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: "10px 12px", background: BG, fontSize: 12, color: MID, lineHeight: 1.6 }}>{metaDesc}</div>
                              <div style={{ height: 4, borderRadius: 2, background: "#e5e7eb", marginTop: 6, overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${Math.min(100, (metaDescLen / 160) * 100)}%`, background: metaDescLen <= 160 ? G : "#dc2626", borderRadius: 2 }} />
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 800, color: "#34a853", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>H1 Heading</div>
                              <div style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: "10px 12px", background: BG, fontSize: 14, fontWeight: 700, color: BLACK }}>{h1}</div>
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: "#34a853", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>Long-Form SEO Description</div>
                            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: "12px 14px", background: BG, fontSize: 12, color: "#374151", lineHeight: 1.7, maxHeight: 200, overflowY: "auto", marginBottom: 12, whiteSpace: "pre-line" }}>{longDesc}</div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: "#34a853", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>GEO Targeting</div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                              {geoTargets.map(g => (
                                <span key={g} style={{ fontSize: 11, fontWeight: 700, color: "#34a853", background: "#f0faf4", border: `1px solid ${GB}`, borderRadius: 5, padding: "4px 10px" }}>{g}</span>
                              ))}
                            </div>
                            <div style={{ padding: "8px 10px", background: GL, border: `1px solid ${GB}`, borderRadius: 5, fontSize: 11, color: G, lineHeight: 1.5 }}>
                              Copy includes geographic modifiers ({geoTargets.join(", ")}) for local search intent. "{geoTargets[0]}" appears in title, meta desc, and body for geo-targeted SERP ranking.
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Meta Ads - BETA */}
                  {(channelTab[p.id]) === "meta" && (
                    <div data-testid={`channel-meta-content-${p.id}`} style={{ padding: "20px 16px 24px", textAlign: "center" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, marginBottom: 12 }}>
                        <span style={{ fontSize: 10, fontWeight: 800, color: "#d97706", letterSpacing: "0.5px" }}>BETA</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>Meta Ads Format Engine</span>
                      </div>
                      <div style={{ fontSize: 13, color: MID, lineHeight: 1.6, maxWidth: 420, margin: "0 auto" }}>
                        Meta Ads copy generation is in beta. This will generate headline, primary text, and description variants optimised for Facebook and Instagram ad formats with dynamic product data.
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
                        {["Headline (40 chars)", "Primary Text (125 chars)", "Description (30 chars)", "CTA Overlay"].map(f => (
                          <span key={f} style={{ fontSize: 10, fontWeight: 600, color: LIGHT, background: "#f9fafb", border: `1px solid ${BORDER}`, borderRadius: 4, padding: "4px 10px" }}>{f}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* TikTok Shop - BETA */}
                  {(channelTab[p.id]) === "tiktok" && (
                    <div data-testid={`channel-tiktok-content-${p.id}`} style={{ padding: "20px 16px 24px", textAlign: "center" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, marginBottom: 12 }}>
                        <span style={{ fontSize: 10, fontWeight: 800, color: "#d97706", letterSpacing: "0.5px" }}>BETA</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>TikTok Shop Format Engine</span>
                      </div>
                      <div style={{ fontSize: 13, color: MID, lineHeight: 1.6, maxWidth: 420, margin: "0 auto" }}>
                        TikTok Shop copy generation is in beta. This will generate product titles and descriptions optimised for TikTok Shop's character limits and discovery algorithm.
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
                        {["Product Title (34 chars)", "Description (1000 chars)", "Category Tags", "Showcase Copy"].map(f => (
                          <span key={f} style={{ fontSize: 10, fontWeight: 600, color: LIGHT, background: "#f9fafb", border: `1px solid ${BORDER}`, borderRadius: 4, padding: "4px 10px" }}>{f}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: LIGHT, letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 8 }}>TAGS</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[...p.signalsUsed, "SS26", "menswear", "premium"].map(tag => (
                      <span key={tag} style={{ fontSize: 11, background: "#f3f4f6", border: `1px solid ${BORDER}`, color: MID, padding: "3px 9px", borderRadius: 4, fontWeight: 500 }}>{tag}</span>
                    ))}
                  </div>
                </div>

                {/* ── Channel Previews ── */}
                <PreviewPanels p={p} openPrev={openPrev} setOpenPrev={setOpenPrev} generatedCopy={generatedCopy} />

              </div>
            )}
          </div>
        );
      })}

      <Footer onBack={onBack} onNext={onNext} nextLabel={`Push ${appr.length} SKU${appr.length !== 1 ? "s" : ""} →`} disabled={appr.length === 0} />
    </>
  );
}

// ─── STEP 06 ─────────────────────────────────────────────────────────────────


